import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import LiteShell from './LiteShell';
import LiteWishlist from '@/components/lite/LiteWishlist';
import { FileText, Mail, FileSignature, Workflow, CalendarDays, Brain, Sparkles, Lock, Home } from 'lucide-react';
import { liteApi } from '@/utils/lite/liteApi';

const TOOLS = [
  // CLINICAL WRITING
  {
    group: 'Clinical Writing',
    items: [
      {
        slug: 'report',
        title: 'Report Builder',
        body: 'Full clinical report drafts from your scores and observations.',
        icon: FileText,
        live: true,
      },
      {
        slug: 'parent',
        title: 'Parent Summary',
        body: 'Translate session notes into warm, plain-language updates.',
        icon: Mail,
        live: true,
      },
      {
        slug: 'referral',
        title: 'Referral / Doctor Letter',
        body: 'Formal referral letters from observations and reasoning.',
        icon: FileSignature,
        live: false,
      },
    ],
  },
  // PLANNING
  {
    group: 'Planning',
    items: [
      {
        slug: 'session-bridge',
        title: 'Session-to-Home Bridge',
        body: 'Turn one set of session notes into clinical reflection, next-session focus, parent handoff, and one realistic home carry-over.',
        icon: Home,
        live: true,
        badge: 'New',
      },
      {
        slug: 'iep',
        title: 'IEP Builder',
        body: 'Present levels, SMART goals, accommodations.',
        icon: Workflow,
        live: false,
      },
      {
        slug: 'activity',
        title: 'Activity Generator',
        body: 'Neurodiversity-affirming play activities tailored to GLP stage, interests, and goals.',
        icon: CalendarDays,
        live: true,
      },
    ],
  },
  // GLP SPECIALIST
  {
    group: 'GLP Support',
    items: [
      {
        slug: 'glp',
        title: 'GLP Support Guide',
        body: 'Pattern-based stage analysis + neurodiversity-affirming strategies.',
        icon: Brain,
        live: true,
      },
    ],
  },
];

export default function LiteHub() {
  const navigate = useNavigate();
  const [usage, setUsage] = useState(null);

  useEffect(() => {
    let email = null;
    try { email = localStorage.getItem('playfirst_lite_email'); } catch {}
    liteApi.usage(email).then(setUsage).catch(() => {});
  }, []);

  const handleClick = (tool) => {
    if (!tool.live) return;
    navigate(`/us/tools/${tool.slug}`);
  };

  return (
    <LiteShell>
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4 }}
      >
        <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
          <div>
            <h1 className="text-3xl sm:text-4xl font-serif text-primary-900">Tools</h1>
            <p className="text-stone-500 mt-1">Pick a tool. Inputs are de-identified before AI processing.</p>
          </div>
          {usage && (
            <div
              className="flex items-center gap-2 text-sm text-stone-700 bg-stone-50 border border-stone-200 rounded-full px-3 py-1.5"
              data-testid="lite-hub-usage-pill"
            >
              <Sparkles className="w-3.5 h-3.5 text-primary" />
              <span>
                <strong>{Math.max(0, usage.remaining)}</strong> of {usage.limit} free generations left today
              </span>
            </div>
          )}
        </div>

        <div className="space-y-10 mt-8">
          {TOOLS.map((group) => (
            <section key={group.group}>
              <h2 className="text-sm font-medium uppercase tracking-wide text-stone-500 mb-3">
                {group.group}
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                {group.items.map((tool) => {
                  const Icon = tool.icon;
                  return (
                    <button
                      key={tool.slug}
                      onClick={() => handleClick(tool)}
                      disabled={!tool.live}
                      data-testid={`lite-tool-card-${tool.slug}`}
                      className={[
                        'group text-left rounded-2xl border bg-white p-5 transition-all',
                        tool.live
                          ? 'border-stone-200 hover:border-primary/40 hover:shadow-sm cursor-pointer'
                          : 'border-stone-100 opacity-70 cursor-not-allowed',
                      ].join(' ')}
                    >
                      <div className="flex items-start justify-between mb-3">
                        <div className="w-10 h-10 rounded-xl bg-primary/10 text-primary flex items-center justify-center">
                          <Icon className="w-5 h-5" />
                        </div>
                        {!tool.live && (
                          <span className="inline-flex items-center gap-1 text-[11px] uppercase tracking-wide text-stone-500 bg-stone-50 border border-stone-200 px-2 py-0.5 rounded-full">
                            <Lock className="w-3 h-3" />
                            Coming soon
                          </span>
                        )}
                        {tool.live && tool.badge && (
                          <span className="inline-flex items-center gap-1 text-[11px] font-semibold uppercase tracking-wide text-violet-700 bg-violet-50 border border-violet-200 px-2 py-0.5 rounded-full">
                            <Sparkles className="w-3 h-3" />
                            {tool.badge}
                          </span>
                        )}
                      </div>
                      <h3 className="font-medium text-primary-900 mb-1 group-hover:text-primary transition-colors">
                        {tool.title}
                      </h3>
                      <p className="text-sm text-stone-500 leading-relaxed">{tool.body}</p>
                    </button>
                  );
                })}
              </div>
            </section>
          ))}
        </div>

        {/* Wishlist — anonymous user feedback, weekly cadence */}
        <div className="mt-10">
          <LiteWishlist variant="card" sourcePage="/us" />
        </div>
      </motion.div>
    </LiteShell>
  );
}
